import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart3, List, Hash, Plus, X, Globe, Lock, ArrowRight, ChevronLeft, Camera, Image as ImageIcon, Shield, Key, Radio, Clock, Calendar, Users, Search, Loader, Info } from 'lucide-react';
import { cn, getRoomPath } from '../utils';
import { useActivities } from '../context/ActivitiesContext';
import { useAuth } from '../context/AuthContext';
import { useCreation } from '../hooks/useCreation';
import { themeClasses } from '../utils/themes';
import { handleImageUpload } from '../utils/imageUtils';

const PollCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const searchParams = new URLSearchParams(location.search);
  const community_id = searchParams.get('community_id');
  const { addRoom } = useActivities();

  const theme = themeClasses['poll'];

  const {
    step,
    setStep,
    loading,
    setLoading,
    error,
    setError,
    image: pollImage,
    setImage: setPollImage,
    fileInputRef,
    speakers,
    speakerInput,
    setSpeakerInput,
    addSpeaker,
    removeSpeaker,
    searchMembers,
    searchResults,
    searching,
    nextStep: triggerNextStep,
    prevStep,
  } = useCreation();

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setError('');
        const resizedImage = await handleImageUpload(file, { maxSizeMB: 2 });
        setPollImage(resizedImage);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const speakerSearchAllowed = Boolean(community_id || user?.role === 'platform_admin');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (speakerInput.trim().length >= 2 && speakerSearchAllowed) {
        searchMembers(speakerInput, community_id, user?.role === 'platform_admin');
      } else {
        searchMembers('', community_id, speakerSearchAllowed);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [speakerInput, community_id, searchMembers, speakerSearchAllowed, user?.role]);

  const [options, setOptions] = useState([
    { text: '', color: 'bg-brand-indigo', image: null },
    { text: '', color: 'bg-purple-500', image: null }
  ]);
  const [poll_type, setPollType] = useState('single');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [timeframe, setTimeframe] = useState('now');
  const [scheduledAt, setScheduledAt] = useState('');
  const [duration, setDuration] = useState('1h');
  const [resultsVisibility, setResultsVisibility] = useState('after_voting');
  const [privacy, setPrivacy] = useState('public');
  const [password, setPassword] = useState('');
  const [inviteOnly, setInviteOnly] = useState(false);

  const TITLE_LIMIT = 100;
  const DESCRIPTION_LIMIT = 500;
  const OPTION_LIMIT = 80;

  const colors = ['bg-brand-indigo', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-sky-500'];

  const handleOptionImageChange = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        setError('');
        const resizedImage = await handleImageUpload(file, { maxSizeMB: 1, maxWidth: 800, maxHeight: 800 });
        const newOptions = [...options];
        newOptions[index].image = resizedImage;
        setOptions(newOptions);
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const addOption = () => {
    if (options.length >= 6) return;
    setOptions([...options, { text: '', color: colors[options.length], image: null }]);
  };
  const removeOption = (index) => setOptions(options.filter((_, i) => i !== index));

  const handleCreate = async () => {
    const validOptions = options.filter(opt => opt.text.trim() !== '');
    const validate = () => {
      if (!title.trim()) return 'Please enter a poll question';
      if (title.length > TITLE_LIMIT) return `Question is too long (max ${TITLE_LIMIT} chars)`;
      if (description.length > DESCRIPTION_LIMIT) return `Description is too long (max ${DESCRIPTION_LIMIT} chars)`;
      if (validOptions.length < 2) return 'Please enter at least two options';
      if (validOptions.some(opt => opt.text.length > OPTION_LIMIT)) return `Some options are too long (max ${OPTION_LIMIT} chars)`;
      if (timeframe === 'scheduled' && !scheduledAt) return 'Please select a scheduled date and time';
      if (privacy === 'private' && !password.trim()) return 'Please set a password for your private poll';
      return null;
    };

    const errorMsg = validate();
    if (errorMsg) {
      setError(errorMsg);
      if (!title.trim() || validOptions.length < 2) setStep(1);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const newRoom = await addRoom({
        title,
        description,
        type: 'Poll',
        poll_type,
        options: validOptions,
        timeframe,
        scheduled_at: timeframe === 'scheduled' ? scheduledAt : null,
        duration,
        resultsVisibility,
        privacy,
        settings: {
          password: privacy === 'private' ? password : null,
          invite_only: privacy === 'private' ? inviteOnly : false,
          speakers: speakers.length > 0 ? speakers.map(s => s.name) : null
        },
        image: pollImage,
        community_id,
        participants: 0,
        host: user?.name || 'You'
      });
      navigate(getRoomPath(newRoom));
    } catch (err) {
      console.error('Room creation failed:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to create poll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    triggerNextStep(() => {
      const validOptions = options.filter(opt => opt.text.trim() !== '');
      if (!title.trim()) return 'Please enter a poll question';
      if (validOptions.length < 2) return 'Please enter at least two options';
      return null;
    });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
      <div className="flex items-center gap-4 mb-10">
        <button 
          onClick={() => step > 1 ? prevStep() : navigate(-1)}
          className="p-3 hover:bg-gray-100 rounded-2xl transition-all text-gray-400 hover:text-gray-900"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <div className="flex items-center gap-2 text-xxs font-bold text-gray-400 uppercase tracking-widest mb-1">
             <Link to="/communities" className="hover:text-brand-indigo transition-colors">Communities</Link>
             <span>/</span>
             <Link to={`/c/${community_id}`} className="hover:text-brand-indigo transition-colors truncate max-w-[100px]">Community</Link>
             <span>/</span>
             <span className="text-brand-indigo">New Poll</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">Create a Live Poll</h1>
          <p className="text-gray-500 font-medium text-sm">Step {step} of 2: {step === 1 ? 'Question & Options' : 'Configuration'}</p>
        </div>
      </div>

      {step === 1 ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-8"
        >
          <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 space-y-8 shadow-xl shadow-gray-100/50">
            {error && (
              <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                <Shield size={16} />
                {error}
              </div>
            )}
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileChange} 
              accept="image/*" 
              className="hidden" 
            />
            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Poll Header Image</label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="relative h-48 rounded-3xl bg-gray-50 border-2 border-dashed border-gray-100 flex flex-col items-center justify-center cursor-pointer group hover:bg-gray-100 transition-all overflow-hidden"
              >
                {pollImage ? (
                  <>
                    <img src={pollImage} alt="Poll cover" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Camera size={32} className="text-white" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-4 rounded-full bg-white shadow-sm mb-2 text-gray-300 group-hover:text-brand-indigo transition-colors">
                      <ImageIcon size={32} />
                    </div>
                    <p className="text-xs font-bold text-gray-400">Add an image to make your poll stand out</p>
                  </>
                )}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">The Question</label>
                <span className={cn("text-xxs font-bold", title.length > TITLE_LIMIT ? "text-red-500" : "text-gray-400")}>
                  {title.length}/{TITLE_LIMIT}
                </span>
              </div>
              <input 
                type="text" 
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, TITLE_LIMIT + 10))}
                placeholder="What would you like to ask?"
                className={cn(
                  "w-full px-6 py-5 bg-brand-indigo/10 border-2 border-brand-indigo/10 rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 focus:border-brand-indigo/20 font-bold transition-all text-xl placeholder:text-gray-300",
                  title.length > TITLE_LIMIT && "border-red-500/50"
                )}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Context / Description</label>
                <span className={cn("text-xxs font-bold", description.length > DESCRIPTION_LIMIT ? "text-red-500" : "text-gray-400")}>
                  {description.length}/{DESCRIPTION_LIMIT}
                </span>
              </div>
              <textarea 
                rows="2"
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_LIMIT + 50))}
                placeholder="Provide some context for your poll..."
                className={cn(
                  "w-full px-6 py-4 bg-gray-50 border-none rounded-2xl focus:ring-2 focus:ring-brand-indigo/20 font-medium transition-all resize-none",
                  description.length > DESCRIPTION_LIMIT && "ring-2 ring-red-500/20"
                )}
              ></textarea>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Speaker(s)</label>
              <div className="flex flex-wrap gap-2 mb-4">
                <div className={cn("px-4 py-2 rounded-xl font-bold text-sm flex items-center gap-2 border", theme.lightBg, theme.text, theme.lightBorder)}>
                  <Shield size={14} />
                  You (Host)
                </div>
                {speakers.map((speaker) => (
                  <div key={speaker.id} className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl font-bold text-sm flex items-center gap-2 border border-gray-100">
                    <div className="w-5 h-5 rounded-full bg-brand-indigo/10 flex items-center justify-center overflow-hidden shrink-0">
                      {speaker.avatar ? <img src={speaker.avatar} alt={speaker.name} className="w-full h-full object-cover" /> : <Users size={12} className="text-brand-indigo" />}
                    </div>
                    {speaker.name}
                    <button onClick={() => removeSpeaker(speaker.id)} className="ml-1 hover:text-red-500 transition-colors font-bold text-gray-400">
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="relative">
                <div className="relative">
                  <input 
                    type="text" 
                    value={speakerInput}
                    onChange={(e) => setSpeakerInput(e.target.value)}
                    placeholder={community_id ? "Search community members..." : speakerSearchAllowed ? "Search platform members..." : "Only platform admins can assign speakers outside a community"}
                    disabled={!speakerSearchAllowed}
                    className={cn(
                      "w-full pl-10 pr-4 py-3 bg-gray-50 border-none rounded-xl font-medium text-sm transition-all focus:ring-2",
                      theme.ring,
                      !speakerSearchAllowed && "cursor-not-allowed opacity-60"
                    )}
                  />
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400">
                    {searching ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
                  </div>
                </div>

                <AnimatePresence>
                  {searchResults.length > 0 && speakerInput.trim().length >= 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-60 overflow-y-auto"
                    >
                      {searchResults.map((member) => (
                        <button
                          key={member.id}
                          onClick={() => addSpeaker(member)}
                          className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 transition-colors text-left"
                        >
                          <div className="w-8 h-8 rounded-full bg-brand-indigo/5 flex items-center justify-center overflow-hidden shrink-0">
                            {member.avatar ? <img src={member.avatar} alt={member.name} className="w-full h-full object-cover" /> : <Users size={16} className="text-brand-indigo" />}
                          </div>
                          <div>
                            <p className="font-bold text-sm text-gray-900">{member.name}</p>
                            <p className="text-xxs font-medium text-gray-500">{member.email}</p>
                          </div>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <p className="text-xxs font-medium text-gray-400 mt-2 flex items-center gap-1.5 px-1">
                <Info size={10} />
                {community_id
                  ? 'Speakers must be current members of this community.'
                  : speakerSearchAllowed
                    ? 'Outside a community, only platform admins can assign additional speakers.'
                    : 'This poll is not linked to a community, so only platform admins may add speakers.'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { id: 'single', label: 'Single Choice', icon: List, desc: 'One vote per person' },
                { id: 'multiple', label: 'Multiple Choice', icon: BarChart3, desc: 'Select many options' },
                { id: 'ranked', label: 'Ranked Choice', icon: Hash, desc: 'Order by preference' },
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setPollType(type.id)}
                  className={cn(
                    "flex flex-col items-start gap-2 p-4 rounded-2xl border-2 transition-all text-left",
                    poll_type === type.id ? "border-brand-indigo bg-brand-indigo/5 text-brand-indigo" : "border-gray-50 bg-white text-gray-400 hover:border-gray-100"
                  )}
                >
                  <div className={cn("p-2 rounded-lg", poll_type === type.id ? "bg-brand-indigo text-white" : "bg-gray-50")}>
                    <type.icon size={20} />
                  </div>
                  <div>
                    <span className="text-[10px] font-bold uppercase tracking-wider block">{type.label}</span>
                    <span className="text-[10px] opacity-70 font-medium block mt-0.5">{type.desc}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest">Poll Options</label>
              <div className="grid grid-cols-1 gap-3">
                {options.map((option, index) => (
                  <div key={index} className="group flex flex-col gap-2">
                    <div className="flex gap-2">
                      <div className={cn("w-1.5 rounded-full my-2", option.color)}></div>
                      <div className="flex-1 relative">
                        <input 
                          type="text" 
                          value={option.text}
                          onChange={(e) => {
                            const newOptions = [...options];
                            newOptions[index].text = e.target.value;
                            setOptions(newOptions);
                          }}
                          placeholder={`Option ${index + 1}`}
                          className={cn(
                            "w-full px-5 py-3.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-brand-indigo/20 font-bold transition-all text-sm pr-16",
                            option.text.length > OPTION_LIMIT && "ring-2 ring-red-500/20"
                          )}
                        />
                        <span className={cn(
                          "absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold",
                          option.text.length > OPTION_LIMIT ? "text-red-500" : "text-gray-300"
                        )}>
                          {option.text.length}/{OPTION_LIMIT}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <input 
                          type="file" 
                          id={`opt-image-${index}`}
                          onChange={(e) => handleOptionImageChange(index, e)}
                          accept="image/*"
                          className="hidden"
                        />
                        <label 
                          htmlFor={`opt-image-${index}`}
                          className={cn(
                            "w-12 h-12 rounded-xl flex items-center justify-center cursor-pointer transition-all border-2",
                            option.image ? "bg-brand-indigo border-brand-indigo text-white shadow-md shadow-brand-indigo/10" : "bg-white border-gray-50 text-gray-400 hover:border-gray-100 hover:text-brand-indigo"
                          )}
                        >
                          <ImageIcon size={18} />
                        </label>
                        {options.length > 2 && (
                          <button 
                            onClick={() => removeOption(index)}
                            className="w-12 h-12 bg-white border-2 border-gray-50 text-gray-300 hover:text-red-500 hover:border-red-50 hover:bg-red-50 rounded-xl transition-all flex items-center justify-center"
                          >
                            <X size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    {option.image && (
                      <div className="ml-3.5 relative w-24 h-16 rounded-lg overflow-hidden group/img border-2 border-brand-indigo/10">
                        <img src={option.image} alt="" className="w-full h-full object-cover" />
                        <button 
                          onClick={() => {
                            const next = [...options];
                            next[index].image = null;
                            setOptions(next);
                          }}
                          className="absolute inset-0 bg-black/40 text-white flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button 
                onClick={addOption}
                disabled={options.length >= 6}
                className="w-full py-3.5 border-2 border-dashed border-gray-100 rounded-xl text-gray-400 font-bold text-xs uppercase tracking-widest hover:border-brand-indigo/20 hover:text-brand-indigo transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus size={16} />
                Add Choice ({6 - options.length} remaining)
              </button>
            </div>
          </div>

          <button 
            onClick={nextStep}
            className="w-full bg-brand-indigo text-white font-bold py-5 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all text-lg"
          >
            Configure Poll
            <ArrowRight size={20} />
          </button>
        </motion.div>
      ) : (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="space-y-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              {error && (
                <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-2">
                  <Shield size={16} />
                  {error}
                </div>
              )}
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">When?</h2>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button 
                    onClick={() => setTimeframe('now')}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all",
                      timeframe === 'now' ? "bg-brand-indigo text-white border-brand-indigo shadow-lg shadow-brand-indigo/20" : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Radio size={22} className={cn("mb-2", timeframe === 'now' && "animate-pulse")} />
                    <span className="font-bold text-sm">Live Now</span>
                  </button>
                  <button 
                    onClick={() => setTimeframe('scheduled')}
                    className={cn(
                      "flex flex-col items-center justify-center p-5 rounded-3xl border-2 transition-all",
                      timeframe === 'scheduled' ? "bg-brand-indigo text-white border-brand-indigo shadow-lg shadow-brand-indigo/20" : "bg-white text-gray-600 border-gray-50 hover:border-gray-100"
                    )}
                  >
                    <Calendar size={22} className="mb-2" />
                    <span className="font-bold text-sm">Schedule</span>
                  </button>
                </div>

                {timeframe === 'scheduled' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mb-4 space-y-3"
                  >
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar size={14} />
                      <label className="text-xxs font-bold uppercase tracking-widest">Poll Date & Time</label>
                    </div>
                    <input 
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      className="w-full px-5 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-brand-indigo/10 font-bold text-sm transition-all"
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </motion.div>
                )}

                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Active Duration</h2>
                <div className="grid grid-cols-3 gap-2">
                  {['5m', '15m', '1h', '24h', 'Always'].map((d) => (
                    <button 
                      key={d} 
                      onClick={() => setDuration(d)}
                      className={cn(
                        "py-3 rounded-xl font-bold transition-all text-xxs uppercase",
                        duration === d ? "bg-brand-indigo text-white shadow-md shadow-brand-indigo/20" : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                      )}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Results Visibility</h2>
                <div className="space-y-2">
                  <button 
                    onClick={() => setResultsVisibility('after_voting')}
                    className={cn(
                      "w-full flex items-center justify-between p-4 rounded-2xl font-bold text-sm transition-all",
                      resultsVisibility === 'after_voting' ? "bg-brand-indigo/10 border-2 border-brand-indigo/10 text-brand-indigo" : "bg-gray-50 border-2 border-transparent text-gray-500 hover:bg-gray-100"
                    )}
                  >
                    Show results after voting
                    {resultsVisibility === 'after_voting' && <div className="w-1.5 h-1.5 bg-brand-indigo rounded-full"></div>}
                  </button>
                  <button 
                    onClick={() => setResultsVisibility('closed')}
                    className={cn(
                      "w-full flex items-center justify-between p-4 rounded-2xl font-bold text-sm transition-all",
                      resultsVisibility === 'closed' ? "bg-brand-indigo/10 border-2 border-brand-indigo/10 text-brand-indigo" : "bg-gray-50 border-2 border-transparent text-gray-500 hover:bg-gray-100"
                    )}
                  >
                    Show results only when closed
                    {resultsVisibility === 'closed' && <div className="w-1.5 h-1.5 bg-brand-indigo rounded-full"></div>}
                  </button>
                </div>
              </section>
            </div>

            <div className="space-y-6">
              <section>
                <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Visibility</h2>
                <div className="space-y-3">
                  <button 
                    onClick={() => setPrivacy('public')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                      privacy === 'public' ? "border-brand-indigo bg-brand-indigo/10" : "border-gray-50 bg-white"
                    )}
                  >
                    <Globe size={20} className={privacy === 'public' ? "text-brand-indigo" : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Public Poll</p>
                      <p className="text-xxs text-gray-500 font-medium">Visible to everyone in ecosystem</p>
                    </div>
                  </button>
                  <button 
                    onClick={() => setPrivacy('private')}
                    className={cn(
                      "w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left",
                      privacy === 'private' ? "border-brand-indigo bg-brand-indigo/10" : "border-gray-50 bg-white"
                    )}
                  >
                    <Lock size={20} className={privacy === 'private' ? "text-brand-indigo" : "text-gray-400"} />
                    <div>
                      <p className="font-bold text-sm text-gray-900">Private Poll</p>
                      <p className="text-xxs text-gray-500 font-medium">Link-only access</p>
                    </div>
                  </button>
                </div>
              </section>

              {privacy === 'private' && (
                <motion.section
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="space-y-4 pt-2"
                >
                  <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Private Access Configuration</h2>
                  <div className="bg-white rounded-[2.5rem] border border-brand-indigo/10 p-6 space-y-5 shadow-sm">
                    <button 
                      onClick={() => setInviteOnly(!inviteOnly)}
                      className="w-full flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-4">
                        <div className={cn("p-2.5 rounded-xl transition-colors", inviteOnly ? "bg-brand-indigo/10 text-brand-indigo" : "bg-gray-50 text-gray-400")}>
                          <Shield size={18} />
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-sm text-gray-900">Member Only</p>
                          <p className="text-xxs text-gray-500 font-medium uppercase tracking-wider">Only community members vote</p>
                        </div>
                      </div>
                      <div className={cn("w-12 h-7 rounded-full relative transition-colors p-1", inviteOnly ? "bg-brand-indigo" : "bg-gray-200")}>
                        <div className={cn("w-5 h-5 bg-white rounded-full transition-all shadow-sm", inviteOnly ? "translate-x-5" : "translate-x-0")}></div>
                      </div>
                    </button>

                    <div className="h-px bg-gray-50 w-full"></div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-gray-400">
                        <Key size={14} />
                        <label className="text-xxs font-bold uppercase tracking-widest">Access Password</label>
                      </div>
                      <input 
                        type="text"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Optional: Set a password"
                        className="w-full px-5 py-3 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-brand-indigo/10 font-bold text-sm transition-all"
                      />
                    </div>
                  </div>
                </motion.section>
              )}
            </div>
          </div>

          <button 
            onClick={handleCreate}
            disabled={loading}
            className={cn(
              "w-full bg-brand-indigo text-white font-bold py-5 rounded-2xl shadow-xl shadow-brand-indigo/10 flex items-center justify-center gap-2 hover:bg-brand-indigo/90 transition-all text-lg",
              loading && "opacity-70 cursor-not-allowed"
            )}
          >
            {loading ? (
              <div className="w-6 h-6 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              "Launch Interactive Poll"
            )}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default PollCreation;
